import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// "Send all the new hire paperwork" on the employee profile (CJ 2026-08-08).
// Managers and owners only.
//
// The server side already existed and had never been wired to a button:
// POST /api/admin/users/:id/send-onboarding-bundle queues the documents,
// prefills each PDF from what's on file, and emails them as attachments. Its
// re-send rules are already sensible — unsent gets sent, sent-but-unsigned is
// bumped so it resurfaces, already-signed gets a fresh copy to re-sign.
//
// A top-level component, not one declared inside EmployeeProfileView's render:
// a component defined in a render body is a new type every keystroke, so React
// remounts it and any focus is lost.
export function SendOnboardingPaperwork({ userId, userName, currentUser, viewMode }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const isManager = !!window.EmpireRoles?.hasCapability?.(currentUser, 'view_admin_panel');
  const [templates, setTemplates] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api('GET', `/api/admin/users/${userId}/onboarding-templates`, null, headers);
      setTemplates(Array.isArray(r?.templates) ? r.templates : []);
    } catch (e) { setErr(e?.message || String(e)); }
  }, [userId, currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only managers/owners may see this at all, and only when actually looking
    // at someone else's profile as a manager.
    if (isManager && viewMode === 'manager' && userId) load();
  }, [isManager, viewMode, userId, load]);

  if (!isManager || viewMode !== 'manager') return null;
  if (!templates) return null;

  const signed = templates.filter(t => t.current_status === 'signed' || t.current_signed_at).length;
  const outstanding = templates.filter(t => t.current_status && t.current_status !== 'signed' && !t.current_signed_at).length;
  const neverSent = templates.filter(t => !t.current_status).length;

  async function send() {
    const ok = window.confirm(
      `Send all new hire paperwork to ${userName}?\n\n` +
      `${templates.length} document${templates.length === 1 ? '' : 's'} will be emailed as pre-filled PDFs.` +
      (signed ? `\n\n${signed} already signed — they'll get a fresh copy to re-sign.` : '')
    );
    if (!ok) return;
    setSending(true); setErr(''); setResult(null);
    try {
      // No template_ids — the server sends every active template matching
      // their role, which is what "all the paperwork" means.
      const r = await api('POST', `/api/admin/users/${userId}/send-onboarding-bundle`, {}, headers);
      setResult(r);
      await load();
    } catch (e) {
      let m = e?.message || 'Could not send.';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setSending(false); }
  }

  // The server distinguishes three outcomes and so does this: actually sent,
  // no address on file, and the send failed. They need different fixes, and
  // reporting a failed send as success is how paperwork silently never arrives.
  function outcome() {
    if (!result) return null;
    if (result.email_sent) {
      return { tone: 'ok', text: `Sent to ${result.recipient_email} — ${result.attachments_sent} PDF${result.attachments_sent === 1 ? '' : 's'} attached.` };
    }
    if (!result.recipient_email) {
      return { tone: 'warn', text: `${userName} has no email address on file. The documents are queued, but nothing was emailed — add an address and send again.` };
    }
    return { tone: 'bad', text: `The documents were queued but the email to ${result.recipient_email} did not go out${result.email_error ? ` (${result.email_error})` : ''}. Try again, or check the address.` };
  }
  const out = outcome();
  const toneColor = { ok: '#2e5d42', warn: '#8a5a1f', bad: '#a8323a' };
  const toneBg = { ok: '#f1f7f2', warn: '#fff8e1', bad: '#faeceb' };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="text-base font-semibold">New hire paperwork</div>
          <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
            {templates.length === 0
              ? 'No document templates are set up yet.'
              : <>{templates.length} document{templates.length === 1 ? '' : 's'} · {signed} signed · {outstanding} awaiting signature · {neverSent} never sent</>}
          </div>
        </div>
        {templates.length > 0 && (
          <button onClick={send} disabled={sending} className="btn-primary" style={{ fontSize: 12.5 }}>
            {sending ? 'Sending…' : 'Send all paperwork'}
          </button>
        )}
      </div>

      {templates.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {templates.map((t) => (
            <div key={t.id} style={{ fontSize: 12, color: '#37474f', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{t.display_name || t.name}</span>
              <span style={{ color: t.current_signed_at ? '#2e5d42' : t.current_status ? '#8a5a1f' : '#9e9e9e', whiteSpace: 'nowrap' }}>
                {t.current_signed_at ? 'signed' : t.current_status ? t.current_status : 'not sent'}
              </span>
            </div>
          ))}
        </div>
      )}

      {out && (
        <div style={{ marginTop: 8, fontSize: 12, color: toneColor[out.tone], background: toneBg[out.tone],
                      borderRadius: 6, padding: '6px 10px' }}>
          {out.text}
        </div>
      )}
      {err && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#a8323a', background: '#faeceb', borderRadius: 6, padding: '6px 10px' }}>
          {err}
        </div>
      )}
    </div>
  );
}
